
import { useState, useEffect, useMemo } from 'react';
import { OddsAnalysisResult } from '@/types/teamTypes';
import { MatchData } from '@/components/team-details/types/selector-types';
import { calculateOddsAnalysis } from '@/components/team-details/stats-display/league-table/LeagueTableStats';
import { useOddsMatches } from './useOddsMatches';

interface OddsAnalysisData {
  homeOddsAnalysisData: OddsAnalysisResult | null;
  awayOddsAnalysisData: OddsAnalysisResult | null;
  matches: MatchData[];
  isLoading: boolean;
  isAnalyzing: boolean;
  error: string | null;
  warning: string | null;
  availableTeams: string[];
}

export const useOddsAnalysisData = (
  selectedCountry: string | null,
  selectedLeague: string | null,
  selectedYears: string[],
  selectedTeam: string | null,
  teamLocation: 'any' | 'home' | 'away' = 'any'
): OddsAnalysisData => {
  const [homeOddsAnalysisData, setHomeOddsAnalysisData] = useState<OddsAnalysisResult | null>(null);
  const [awayOddsAnalysisData, setAwayOddsAnalysisData] = useState<OddsAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const { 
    data: matchesData, 
    isLoading: isLoadingMatches, 
    error: queryError 
  } = useOddsMatches(selectedCountry, selectedLeague, selectedYears, selectedTeam);

  // Extract unique teams from matches data - only compute when matches data changes
  const availableTeams = useMemo(() => {
    if (!matchesData || matchesData.length === 0) return [];
    
    const teams = new Set<string>();
    for (let i = 0; i < matchesData.length; i++) {
      const match = matchesData[i];
      if (match.HomeTeam) teams.add(match.HomeTeam);
      if (match.AwayTeam) teams.add(match.AwayTeam);
    }
    
    return Array.from(teams).sort();
  }, [matchesData]);

  // Filter matches by team location when a team is selected - memoize the result
  const filteredMatches = useMemo(() => {
    if (!matchesData) return [];
    if (!selectedTeam || teamLocation === 'any') return matchesData;
    
    return matchesData.filter(match => {
      if (teamLocation === 'home') return match.HomeTeam === selectedTeam;
      if (teamLocation === 'away') return match.AwayTeam === selectedTeam;
      return true;
    });
  }, [matchesData, selectedTeam, teamLocation]);

  // Calculate odds analysis with debouncing to avoid blocking the UI
  useEffect(() => {
    setError(null);
    setWarning(null);
    
    if (!filteredMatches || filteredMatches.length === 0) {
      if (selectedYears.length > 0 && !isLoadingMatches) {
        setError('No matches found for the selected criteria');
      }
      setHomeOddsAnalysisData(null);
      setAwayOddsAnalysisData(null);
      return;
    }
    
    // Use a short delay to prevent UI blocking
    setIsAnalyzing(true);
    const analysisTimer = setTimeout(() => {
      try {
        const homeAnalysisResult = calculateOddsAnalysis(filteredMatches, 'home');
        const awayAnalysisResult = calculateOddsAnalysis(filteredMatches, 'away');
        
        if (homeAnalysisResult.totalMatches === 0 && awayAnalysisResult.totalMatches === 0) {
          setWarning('No matches with valid betting odds data found for the selected seasons.');
          setHomeOddsAnalysisData(null);
          setAwayOddsAnalysisData(null);
        } else {
          // Check data quality
          const oddsDataQuality = checkOddsDataQuality(filteredMatches);
          if (oddsDataQuality < 0.8) {  // Less than 80% of matches have odds data
            setWarning(`Only ${Math.round(oddsDataQuality * 100)}% of matches have valid odds data. Results may not be fully representative.`);
          } else {
            setWarning(null);
          }
          setHomeOddsAnalysisData(homeAnalysisResult);
          setAwayOddsAnalysisData(awayAnalysisResult);
        }
        setIsAnalyzing(false);
      } catch (err) {
        console.error('Error analyzing odds data:', err);
        setError(`Error analyzing odds data: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setHomeOddsAnalysisData(null);
        setAwayOddsAnalysisData(null);
        setIsAnalyzing(false);
      }
    }, 10);
    
    return () => clearTimeout(analysisTimer);
  }, [filteredMatches, isLoadingMatches, selectedYears.length, teamLocation]);

  // Helper function to check odds data quality
  const checkOddsDataQuality = (matches: MatchData[]): number => {
    const matchesWithHomeOdds = matches.filter(match => {
      const oddsValue = typeof match.B365H === 'string' ? parseFloat(match.B365H) : match.B365H;
      return !isNaN(oddsValue);
    }).length;
    
    const matchesWithAwayOdds = matches.filter(match => {
      const oddsValue = typeof match.B365A === 'string' ? parseFloat(match.B365A) : match.B365A;
      return !isNaN(oddsValue);
    }).length;
    
    return matches.length > 0 ? Math.min(matchesWithHomeOdds, matchesWithAwayOdds) / matches.length : 0;
  };

  const showError = error || (queryError ? (queryError as Error).message : null);

  return {
    homeOddsAnalysisData,
    awayOddsAnalysisData,
    matches: filteredMatches,
    isLoading: isLoadingMatches || isAnalyzing,
    isAnalyzing,
    error: showError,
    warning,
    availableTeams
  };
};
